import { isDebugMode } from 'utils/ui/debug';

export type TLogLevel = 'debug' | 'log' | 'warn' | 'error' | 'off';

/**
 * Sink for the Session Flight Recorder (AVS-4426).
 *
 * `willLog` is the level verdict `shouldLog()` is about to return, so a lane can tell "this line
 * reached the console" from "this line was suppressed" — both are evidence, and only one of them
 * survives in a screenshot.
 */
export type TLogSink = (level: TLogLevel, messages: unknown[], willLog: boolean) => void;

function noopLogSink(): void {
  // Default sink: no recorder installed.
}

let logSink: TLogSink = noopLogSink;

/**
 * Register a sink for every log call. Pass `null` to restore the no-op.
 *
 * Called from inside `Logger.shouldLog()` on EVERY call **regardless of the level verdict**. That
 * is the whole point: the 68 `log.*` lines in `webrtc/index.ts` were printing in all six browsers
 * on 2026-09-04 and were discarded, and on a build where the level suppressed them they would not
 * even have printed. Capturing at the verdict, not after it, means the recorder sees them either
 * way without a single call site changing.
 *
 * Same discipline as `setListenerGuardReporter` in `utils/events.ts:61`: a settable module-level
 * function defaulting to a no-op, called from inside the class, wrapped in its own try/catch so a
 * throwing sink cannot fail a log call. A suite that installs one restores it in its own `afterEach`
 * (see `telemetry/recorder/logSink.test.ts`).
 */
export function setLogSink(sink: TLogSink | null): void {
  logSink = sink ?? noopLogSink;
}

interface ILoggerConfig {
  level: TLogLevel;
  prefix?: string;
  prefixColor?: string;
  disabledPrefixes?: string[];
}

export class Logger {
  private level: TLogLevel;
  private prefix?: string;
  private prefixColor?: string = '#ff9900';
  private disabledPrefixes: string[];
  private prefixColorMap: Record<string, string> = {};
  private defaultPrefixColor: string = 'inherit';

  constructor(config: ILoggerConfig) {
    this.level = config.level;
    this.prefix = config.prefix;
    this.prefixColor = config.prefixColor;
    this.disabledPrefixes = config.disabledPrefixes || [];
  }

  private shouldLog(level: TLogLevel, prefix?: string, messages: unknown[] = []): boolean {
    const levels: TLogLevel[] = ['debug', 'log', 'warn', 'error', 'off'];
    const currentLevelIndex = levels.indexOf(this.level);
    const messageLevelIndex = levels.indexOf(level);

    const disabledByPrefix = Boolean(prefix && this.disabledPrefixes.includes(prefix));
    const verdict = !disabledByPrefix && messageLevelIndex >= currentLevelIndex;

    // AVS-4426 recorder sink, on every call and both sides of the verdict. The sink is registered
    // from outside and arbitrary — a throwing one must never make a log call fail.
    try {
      logSink(level, messages, verdict);
    } catch (error) {
      console.error('[Logger] log sink threw and was isolated', error);
    }

    return verdict;
  }

  private formatMessage(message: unknown): unknown {
    if (typeof message !== 'string') {
      return message;
    }
    const prefix = this.extractPrefix(message);
    if (this.prefix) {
      return `%c[${prefix}] ${message}`;
    }
    return message;
  }

  private getColor(message: unknown): string {
    if (typeof message !== 'string') {
      return this.defaultPrefixColor;
    }
    const prefix = this.extractPrefix(message);
    return prefix && this.prefixColorMap[prefix] ? this.prefixColorMap[prefix] : this.defaultPrefixColor;
  }

  private extractPrefix(message: unknown): string | undefined {
    if (typeof message !== 'string') {
      return;
    }
    const match = message.match(/\[(.*?)\]/);
    if (match) {
      return match[1];
    }
    return this.prefix;
  }

  debug(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('debug', prefix, messages)) {
      console.debug(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }

  info(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('log', prefix, messages)) {
      console.info(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }
  log(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('log', prefix, messages)) {
      console.log(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }

  warn(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('warn', prefix, messages)) {
      console.warn(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }

  error(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('error', prefix, messages)) {
      console.error(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }

  updateConfig(config: Partial<ILoggerConfig>): void {
    if (config.prefixColor) {
      this.prefixColor = config.prefixColor;
    }
    if (config.disabledPrefixes) {
      this.disabledPrefixes = config.disabledPrefixes;
    }
  }

  setPrefixColor(prefix: string, color: string): void {
    this.prefixColorMap[prefix] = color;
  }
}

const VALID_LOG_LEVELS: TLogLevel[] = ['debug', 'log', 'warn', 'error', 'off'];

const isValidLogLevel = (value: string | null | undefined): value is TLogLevel =>
  value != null && (VALID_LOG_LEVELS as string[]).includes(value);

export const getLogLevelFromEnv = (): TLogLevel => {
  const envLevel = process.env.LOG_LEVEL;
  const urlParams = new URLSearchParams(window.location.search);
  const urlLevel = urlParams.get('log');

  if (isValidLogLevel(urlLevel)) {
    return urlLevel;
  }

  if (isValidLogLevel(envLevel)) {
    return envLevel;
  }

  return isDebugMode() ? 'debug' : 'off';
};

const logger = new Logger({
  level: getLogLevelFromEnv(),
  prefixColor: 'blue',
  disabledPrefixes: ['NoLog'],
});

logger.setPrefixColor('VOIP', 'gold');
logger.setPrefixColor('SPEAKER DETECTOR', 'salmon');

export default logger;
